import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Collapse from "@material-ui/core/Collapse";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import CopyIcon from "@material-ui/icons/FileCopyOutlined";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import toastError from "../../errors/toastError";
import { useConfirm } from "../../components/ConfirmDialog";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  row: { display: "flex", gap: theme.spacing(1), marginBottom: theme.spacing(2) },
  method: { width: 120 },
  url: { flex: 1 },
  body: { fontFamily: "monospace", fontSize: 12 },
  hint: { color: theme.app.textTertiary, fontSize: 12, marginTop: theme.spacing(1) },
  subtitle: { color: theme.app.textTertiary, fontSize: 12.5, lineHeight: 1.5, marginTop: -6, marginBottom: theme.spacing(2) },
  curlToggle: {
    display: "flex", alignItems: "center", gap: 4, marginTop: theme.spacing(2),
    border: "none", background: "transparent", cursor: "pointer", padding: "4px 0",
    fontSize: 12, fontWeight: 600, color: theme.app.textTertiary, fontFamily: "inherit",
    "& svg": { fontSize: 17 }, "&:hover": { color: theme.app.ink },
  },
  resultHead: { display: "flex", alignItems: "center", gap: 8, marginTop: theme.spacing(2), marginBottom: 4 },
  resultLabel: { fontSize: 12, fontWeight: 600, color: theme.app.ink2 || "#5B6470" },
  statusPill: { fontFamily: theme.app.mono || "monospace", fontSize: 11, fontWeight: 700, borderRadius: 999, padding: "1px 8px", color: "#fff" },
  resultBox: {
    fontFamily: "monospace", fontSize: 11.5, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-all",
    background: "#F6F7F9", color: theme.app.ink, border: `1px solid ${theme.app.border}`, borderRadius: 8,
    padding: "10px 12px", margin: 0, maxHeight: 180, overflow: "auto",
  },
  seg: { display: "inline-flex", background: "#EEF0F3", borderRadius: 8, padding: 2, marginBottom: theme.spacing(1.5) },
  segBtn: {
    border: "none", background: "transparent", cursor: "pointer", fontSize: 12.5, fontWeight: 600,
    color: theme.app.textTertiary, padding: "5px 14px", borderRadius: 6, fontFamily: "inherit",
  },
  segOn: { background: "#fff", color: theme.app.ink, boxShadow: "0 1px 3px rgba(0,0,0,.08)" },
  kvRow: { display: "flex", gap: theme.spacing(1), alignItems: "center", marginBottom: theme.spacing(1) },
  kvKey: { width: "40%" },
  kvVal: { flex: 1 },
  addBtn: { textTransform: "none", fontSize: 12.5, fontWeight: 600, marginTop: 4 },
  curlHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: theme.spacing(2), marginBottom: 4 },
  curlLabel: { fontSize: 12, fontWeight: 600, color: theme.app.ink2 || "#5B6470" },
  curlCopy: { textTransform: "none", fontSize: 12, fontWeight: 600, "& svg": { fontSize: 15 } },
  curlBox: {
    fontFamily: "monospace", fontSize: 11.5, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-all",
    background: "#0F141A", color: "#E6E9ED", borderRadius: 8, padding: "10px 12px", margin: 0, maxHeight: 160, overflow: "auto",
  },
}));

const METHODS = ["POST", "GET", "PUT", "PATCH", "DELETE"];

// coage o valor de um campo visual: número/booleano/JSON viram tipados; senão texto.
const coerce = raw => {
  const s = String(raw == null ? "" : raw).trim();
  if (s === "") return "";
  if (/^-?\d+(\.\d+)?$/.test(s) || s === "true" || s === "false" || s === "null" || /^[[{]/.test(s)) {
    try {
      return JSON.parse(s);
    } catch (e) {
      return raw;
    }
  }
  return raw;
};

// objeto -> pares [{k,v}] (v como string editável). Só top-level; valores
// objeto/array viram JSON string (edite no modo JSON para aninhar).
const objToPairs = obj => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return [];
  return Object.keys(obj).map(k => ({
    k,
    v: typeof obj[k] === "object" ? JSON.stringify(obj[k]) : String(obj[k]),
  }));
};
const pairsToObj = pairs => {
  const o = {};
  pairs.forEach(({ k, v }) => {
    if (String(k).trim()) o[k] = coerce(v);
  });
  return o;
};
const isFlatObject = obj =>
  obj && typeof obj === "object" && !Array.isArray(obj);

// Sends a REAL request to the webhook URL — the generated event is a real,
// internally-produced event (no deploy needed), which then shows up in Events.
// `initialBody` (opcional) é o payload que a IA já preencheu ao criar o gatilho.
const TriggerTestModal = ({ open, webhookUrl, secret, initialBody, published, dirty, onPublish, onClose, onSent }) => {
  const classes = useStyles();
  const { confirm } = useConfirm();
  const [method, setMethod] = useState("POST");
  const [mode, setMode] = useState("visual"); // visual | json
  const [bodyText, setBodyText] = useState('{\n  "num_destino": "+5511999998888"\n}');
  const [pairs, setPairs] = useState([{ k: "num_destino", v: "+5511999998888" }]);
  const [sending, setSending] = useState(false);
  const [showCurl, setShowCurl] = useState(false); // cURL colapsado (avançado)
  const [result, setResult] = useState(null); // { ok, status, statusText, body, error }

  const storeKey = webhookUrl ? `nb:webhookTest:${webhookUrl}` : null;

  // aplica um objeto/texto ao estado (mantém visual e JSON em sincronia)
  const applyBody = useCallback(obj => {
    const text = JSON.stringify(obj, null, 2);
    setBodyText(text);
    setPairs(objToPairs(obj));
    // se o objeto não for "plano", cai pro modo JSON (visual não representa aninhado)
    setMode(isFlatObject(obj) ? "visual" : "json");
  }, []);

  // ao abrir: prioridade localStorage salvo > valor inicial da IA > default atual
  useEffect(() => {
    if (!open) return;
    setResult(null); // limpa resultado de um teste anterior
    let saved = null;
    if (storeKey) {
      try {
        saved = JSON.parse(localStorage.getItem(storeKey) || "null");
      } catch (e) {
        saved = null;
      }
    }
    if (saved && typeof saved.bodyText === "string") {
      if (saved.method) setMethod(saved.method);
      try {
        applyBody(JSON.parse(saved.bodyText));
      } catch (e) {
        setBodyText(saved.bodyText);
        setMode("json");
      }
    } else if (initialBody && typeof initialBody === "object") {
      applyBody(initialBody);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, storeKey]);

  const persist = (nextBody, nextMethod) => {
    if (!storeKey) return;
    try {
      localStorage.setItem(
        storeKey,
        JSON.stringify({ bodyText: nextBody != null ? nextBody : bodyText, method: nextMethod || method })
      );
    } catch (e) {
      /* ignore */
    }
  };

  // edições no modo VISUAL → atualiza pares + serializa pra bodyText
  const setPair = (i, field, val) => {
    setPairs(prev => {
      const next = prev.map((p, idx) => (idx === i ? { ...p, [field]: val } : p));
      const text = JSON.stringify(pairsToObj(next), null, 2);
      setBodyText(text);
      persist(text);
      return next;
    });
  };
  const addPair = () => setPairs(prev => [...prev, { k: "", v: "" }]);
  const removePair = i =>
    setPairs(prev => {
      const next = prev.filter((_, idx) => idx !== i);
      const text = JSON.stringify(pairsToObj(next), null, 2);
      setBodyText(text);
      persist(text);
      return next;
    });

  // edição no modo JSON → atualiza texto (e tenta refletir nos pares)
  const onJsonChange = e => {
    const text = e.target.value;
    setBodyText(text);
    persist(text);
  };

  const switchMode = next => {
    if (next === mode) return;
    if (next === "visual") {
      // ao voltar pro visual, tenta parsear o JSON atual em pares
      try {
        const obj = bodyText.trim() ? JSON.parse(bodyText) : {};
        if (isFlatObject(obj)) {
          setPairs(objToPairs(obj));
          setMode("visual");
          return;
        }
        toast.info("JSON aninhado — edite no modo JSON.");
        return;
      } catch (e) {
        toast.error(i18n.t("triggers.invalidJson"));
        return;
      }
    }
    setMode("json");
  };

  // monta o cURL equivalente ao teste (método, URL, headers e corpo atuais).
  // Inclui o secret (é o app do próprio usuário) para o comando funcionar de fato.
  const buildCurl = () => {
    const url = webhookUrl || "<URL do webhook — salve o fluxo>";
    const lines = [`curl -X ${method} '${url}'`, `  -H 'Content-Type: application/json'`];
    if (secret) lines.push(`  -H 'X-Webhook-Secret: ${secret}'`);
    if (method !== "GET") {
      let oneLine = bodyText;
      try {
        oneLine = JSON.stringify(JSON.parse(bodyText || "{}"));
      } catch (e) {
        /* mantém como está se não for JSON válido */
      }
      lines.push(`  -d '${oneLine}'`);
    }
    return lines.join(" \\\n");
  };
  const copyCurl = () => {
    navigator.clipboard.writeText(buildCurl());
    toast.success("cURL copiado!");
  };

  const handleSend = async () => {
    let data;
    if (method !== "GET") {
      try {
        data = bodyText ? JSON.parse(bodyText) : {};
      } catch (e) {
        toast.error(i18n.t("triggers.invalidJson"));
        return;
      }
    }
    // o teste dispara o fluxo PUBLICADO. Se ainda não foi publicado — OU se há
    // alterações não publicadas (dirty) — oferece publicar antes, senão o teste
    // rodaria uma versão diferente da que está no editor.
    if (!published || dirty) {
      const ok = await confirm({
        title: "Publicar antes de testar?",
        message: !published
          ? "Este fluxo ainda não foi publicado. O teste dispara a versão publicada — publique-o para testar de verdade."
          : "Há alterações não publicadas. O teste dispara a versão publicada — publique para testar o fluxo que está no editor.",
        confirmText: "Publicar e testar",
      });
      if (!ok) return;
      const res = onPublish ? await onPublish() : { ok: false };
      if (!res || !res.ok) {
        toast.error(
          (res && res.error) ||
            "Não foi possível publicar. Verifique os avisos no editor (nome/validação)."
        );
        return;
      }
    }
    setSending(true);
    setResult(null);
    try {
      const resp = await axios.request({
        method,
        url: webhookUrl,
        data,
        headers: {
          "Content-Type": "application/json",
          ...(secret ? { "X-Webhook-Secret": secret } : {}),
        },
        validateStatus: () => true, // não lança em 4xx/5xx — mostramos como resultado
      });
      const ok = resp.status >= 200 && resp.status < 300;
      const body =
        typeof resp.data === "string" ? resp.data : JSON.stringify(resp.data, null, 2);
      // mensagem amigável do backend (ex.: limite de plano no 402) tem prioridade
      // sobre o texto cru do status ("Payment Required")
      const apiMsg =
        resp.data && typeof resp.data === "object"
          ? resp.data.error || resp.data.message
          : null;
      setResult({ ok, status: resp.status, statusText: resp.statusText || "", body });
      if (ok) toast.success(i18n.t("triggers.testSent"));
      else toast.error(apiMsg || `${resp.status} ${resp.statusText || ""}`.trim());
      onSent && (await onSent());
    } catch (err) {
      setResult({ ok: false, error: err.message || "Falha na requisição" });
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{i18n.t("triggers.testEvent")}</DialogTitle>
      <DialogContent>
        <Typography className={classes.subtitle}>{i18n.t("triggers.testHint")}</Typography>
        <div className={classes.row}>
          <TextField
            className={classes.method}
            select
            label={i18n.t("triggers.method")}
            value={method}
            onChange={e => {
              setMethod(e.target.value);
              persist(null, e.target.value);
            }}
            variant="outlined"
            size="small"
          >
            {METHODS.map(m => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            className={classes.url}
            label="URL"
            value={webhookUrl || ""}
            variant="outlined"
            size="small"
            InputProps={{ readOnly: true, style: { fontFamily: "monospace", fontSize: 12 } }}
          />
        </div>

        {method !== "GET" && (
          <>
            <div className={classes.seg}>
              <button
                type="button"
                className={`${classes.segBtn} ${mode === "visual" ? classes.segOn : ""}`}
                onClick={() => switchMode("visual")}
              >
                Visual
              </button>
              <button
                type="button"
                className={`${classes.segBtn} ${mode === "json" ? classes.segOn : ""}`}
                onClick={() => switchMode("json")}
              >
                JSON
              </button>
            </div>

            {mode === "visual" ? (
              <div>
                {pairs.map((p, i) => (
                  <div key={i} className={classes.kvRow}>
                    <TextField
                      className={classes.kvKey}
                      placeholder="chave"
                      value={p.k}
                      onChange={e => setPair(i, "k", e.target.value)}
                      variant="outlined"
                      size="small"
                    />
                    <TextField
                      className={classes.kvVal}
                      placeholder="valor"
                      value={p.v}
                      onChange={e => setPair(i, "v", e.target.value)}
                      variant="outlined"
                      size="small"
                    />
                    <IconButton size="small" onClick={() => removePair(i)} title="Remover">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}
                <Button className={classes.addBtn} size="small" startIcon={<AddIcon />} onClick={addPair}>
                  Adicionar campo
                </Button>
              </div>
            ) : (
              <TextField
                label={i18n.t("triggers.exampleBody")}
                value={bodyText}
                onChange={onJsonChange}
                fullWidth
                multiline
                rows={8}
                variant="outlined"
                size="small"
                InputProps={{ className: classes.body }}
              />
            )}
          </>
        )}
        {/* resultado da última requisição de teste */}
        {result && (
          <>
            <div className={classes.resultHead}>
              <span className={classes.resultLabel}>Resultado</span>
              {result.error ? (
                <span className={classes.statusPill} style={{ background: "#C0453B" }}>erro</span>
              ) : (
                <span
                  className={classes.statusPill}
                  style={{ background: result.ok ? "#3E8E41" : "#C0453B" }}
                >
                  {result.status} {result.statusText}
                </span>
              )}
            </div>
            <pre className={classes.resultBox}>{result.error || result.body || "(sem corpo)"}</pre>
          </>
        )}

        {/* cURL — colapsado por padrão (só usuário avançado precisa) */}
        <button type="button" className={classes.curlToggle} onClick={() => setShowCurl(v => !v)}>
          {showCurl ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          cURL equivalente (avançado)
        </button>
        <Collapse in={showCurl}>
          <div className={classes.curlHead}>
            <span className={classes.curlLabel}>Requisição</span>
            <Button size="small" className={classes.curlCopy} startIcon={<CopyIcon />} onClick={copyCurl}>
              Copiar cURL
            </Button>
          </div>
          <pre className={classes.curlBox}>{buildCurl()}</pre>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{i18n.t("apps.close")}</Button>
        <Button variant="contained" color="secondary" onClick={handleSend} disabled={sending || !webhookUrl}>
          {i18n.t("triggers.sendTest")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TriggerTestModal;
